import { getRgbaValue } from '../tokens/colors';
import { palette } from '../tokens/palette';
import { darken, getContrastRatio, lighten } from './colorUtils';
import type { BrandConfig } from './types';

const pickOnPrimary = (bg: string): string => {
  const w = '#ffffff';
  const b = '#161616';
  if (getContrastRatio(bg, w) >= 4.5) return w;
  if (getContrastRatio(bg, b) >= 4.5) return b;
  return w;
};

const fontVars = (brand: BrandConfig): Record<string, string> => {
  const sans = brand.fontFamily ?? '"HK Grotesk", sans-serif';
  const heading = brand.headingFontFamily ?? sans;
  const mono = brand.monoFontFamily ?? '"JetBrains Mono", "Fira Code", ui-monospace, monospace';
  return {
    '--ui-font-sans': sans,
    '--ui-font-heading': heading,
    '--ui-font-mono': mono,
    '--ui-font-family': sans,
    '--ui-font-mono-family': mono,
  };
};

export function lightAppearance(brand: BrandConfig): Record<string, string> {
  const primary = brand.primary;
  const secondary = brand.secondary ?? palette.Secondary;
  const destructive = palette.Red;

  const primaryHover = darken(primary, 0.08);
  const primaryActive = darken(primary, 0.14);
  const secondaryHover = darken(secondary, 0.08);
  const secondaryActive = darken(secondary, 0.14);
  const destructiveHover = darken(destructive, 0.1);

  const onPrimary = pickOnPrimary(primary);
  const onSecondary = pickOnPrimary(secondary);

  const primaryLight = palette.LightPurple;
  const focusRing = brand.accent ?? palette.Purple;
  const focusRingAlpha = getRgbaValue(focusRing, 0.25);

  const page = palette.OffWhite;
  const surface = palette.White;
  const elevated = palette.gray[50];
  const inset = palette.CulturedWhite;
  const overlay = getRgbaValue('#000000', 0.85);

  const textDefault = palette.Black;
  const textMuted = brand.neutral ?? palette.Gray;
  const textSubtle = palette.SpanishGray;
  const textInverse = palette.White;
  const textDisabled = palette.gray[400];

  const borderDefault = palette.LightGray;
  const borderSubtle = palette.CulturedWhite;
  const borderStrong = palette.Silver;

  return {
    ...fontVars(brand),

    '--ui-background': page,
    '--ui-background-muted': inset,
    '--ui-surface': surface,
    '--ui-surface-elevated': elevated,

    '--ui-text': textDefault,
    '--ui-text-muted': textMuted,
    '--ui-text-subtle': textSubtle,
    '--ui-text-inverse': textInverse,
    '--ui-text-disabled': textDisabled,

    '--ui-border': borderDefault,
    '--ui-border-muted': borderSubtle,
    '--ui-border-strong': borderStrong,

    '--ui-interactive': primary,
    '--ui-interactive-hover': primaryHover,
    '--ui-interactive-active': primaryActive,
    '--ui-on-interactive': onPrimary,
    '--ui-interactive-light': primaryLight,

    '--ui-focus-ring': focusRingAlpha,

    '--ui-status-success': palette.Green,
    '--ui-status-success-muted': palette.LightGreen,
    '--ui-status-warning': '#F5A623',
    '--ui-status-warning-muted': palette.CornSilk,
    '--ui-status-danger': destructive,
    '--ui-status-danger-muted': palette.LightRed,
    '--ui-status-info': palette.Iris,
    '--ui-status-info-muted': palette.LightBlue,

    '--ui-overlay': overlay,
    '--ui-shadow-color': getRgbaValue('#000000', 0.12),
    '--ui-shadow-default': 'rgba(203, 186, 201, 0.12)',
    '--ui-shadow-hover': 'rgba(122, 132, 214, 0.23)',
    '--ui-scrollbar-track': '#f1f1f1',
    '--ui-scrollbar-thumb': palette.SpanishGray,
    '--ui-scrollbar-thumb-hover': '#555555',

    '--ui-table-header-color': palette.Silver,
    '--ui-table-cell-color': palette.Black,
    '--ui-table-border': borderDefault,
    '--ui-table-hover': '#d9d9d91a',
    '--ui-table-header-bg': palette.White,

    '--ui-pagination-active-bg': primaryLight,
    '--ui-pagination-active-color': primary,
    '--ui-pagination-text': textMuted,
    '--ui-pagination-muted': palette.Silver,

    '--ui-badge-icon-border': primary,
    '--ui-badge-icon-color': primary,
    '--ui-badge-hover': primaryLight,
    '--ui-badge-count-bg': destructive,

    '--ui-breadcrumb-active': palette.Iris,
    '--ui-breadcrumb-inactive': palette.Silver,
    '--ui-breadcrumb-link': palette.Iris,
    '--ui-breadcrumb-separator': palette.SpanishGray,

    '--ui-upload-icon-color': palette.Iris,
    '--ui-upload-border': palette.LightGray,
    '--ui-upload-border-error': destructive,
    '--ui-upload-bg': palette.White,
    '--ui-upload-preview-bg': palette.OffWhite,

    '--ui-btn-accent': palette.Iris,
    '--ui-btn-accent-light': palette.LightPurple,
    '--ui-btn-outline-border': getRgbaValue(palette.Iris, 0.3),
    '--ui-btn-red-outline-border': getRgbaValue(palette.Red, 0.3),
    '--ui-btn-red-shadow-hover': getRgbaValue(palette.LightGray, 0.24),
    '--ui-btn-red-shadow-active': getRgbaValue(palette.Red, 0.44),
    '--ui-btn-shadow': getRgbaValue(palette.Iris, 0.24),
    '--ui-btn-disabled-bg': inset,
    '--ui-btn-disabled-color': getRgbaValue(palette.Silver, 0.5),

    '--ui-input-border': borderDefault,
    '--ui-input-border-focus': palette.Blue,
    '--ui-input-shadow': getRgbaValue(palette.gray[400], 0.2),

    '--ui-tab-active-bg': primary,
    '--ui-tab-inactive-bg': palette.OffWhite,
    '--ui-tab-active-color': palette.White,
    '--ui-tab-inactive-color': palette.Gray,

    '--ui-separator-color': borderDefault,

    '--ui-spinner-border': primary,
    '--ui-spinner-track': borderDefault,

    '--ui-background-page': page,
    '--ui-background-surface': surface,
    '--ui-background-elevated': elevated,
    '--ui-background-inset': inset,

    '--ui-text-default': textDefault,
    '--ui-text-on-primary': onPrimary,

    '--ui-border-default': borderDefault,
    '--ui-border-subtle': borderSubtle,
    '--ui-border-focus': focusRingAlpha,

    '--ui-interactive-primary': primary,
    '--ui-interactive-primary-hover': primaryHover,
    '--ui-interactive-primary-active': primaryActive,
    '--ui-interactive-secondary': secondary,
    '--ui-interactive-secondary-hover': secondaryHover,
    '--ui-interactive-secondary-active': secondaryActive,
    '--ui-on-secondary': onSecondary,

    '--ui-destructive': destructive,
    '--ui-destructive-hover': destructiveHover,

    '--ui-status-success-bg': palette.LightGreen,
    '--ui-status-warning-bg': palette.CornSilk,
    '--ui-status-error': destructive,
    '--ui-status-error-bg': palette.LightRed,
    '--ui-status-info-bg': palette.LightBlue,

    '--ui-chart-text': '#333333',
    '--ui-chart-grid': palette.CulturedWhite,
    '--ui-chart-axis': palette.LightGray,
    '--ui-chart-tooltip-bg': palette.White,
    '--ui-chart-tooltip-border': palette.SpanishGray,
    '--ui-chart-positive': '#34a853',
    '--ui-chart-negative': '#ea4335',
    '--ui-chart-1': palette.Purple,
    '--ui-chart-2': palette.Iris,
    '--ui-chart-3': palette.Yellow,
    '--ui-chart-4': palette.SkyBlue,
    '--ui-chart-5': palette.LightBlue,
    '--ui-chart-6': palette.Pink,
    '--ui-chart-7': palette.Green,
    '--ui-chart-8': palette.Secondary,
  };
}

export function darkAppearance(brand: BrandConfig): Record<string, string> {
  const primary = brand.primary;
  const secondary = brand.secondary ?? palette.Secondary;
  const destructive = palette.red[400];

  const primaryHover = lighten(primary, 0.1);
  const primaryActive = lighten(primary, 0.06);
  const secondaryHover = lighten(secondary, 0.08);
  const secondaryActive = lighten(secondary, 0.04);
  const destructiveHover = lighten(destructive, 0.08);

  const onPrimary = pickOnPrimary(primary);
  const onSecondary = pickOnPrimary(secondary);

  const primaryLight = darken(primary, 0.6);
  const focusRing = lighten(brand.accent ?? palette.Purple, 0.15);
  const focusRingAlpha = getRgbaValue(focusRing, 0.35);

  const page = palette.gray[950];
  const surface = palette.gray[900];
  const elevated = palette.gray[800];
  const inset = palette.gray[900];
  const overlay = getRgbaValue('#000000', 0.65);

  const textDefault = palette.gray[50];
  const textMuted = palette.gray[300];
  const textSubtle = palette.gray[500];
  const textInverse = palette.Black;
  const textDisabled = palette.gray[600];

  const borderDefault = palette.gray[700];
  const borderSubtle = palette.gray[800];
  const borderStrong = palette.gray[500];

  return {
    ...fontVars(brand),

    '--ui-background': page,
    '--ui-background-muted': inset,
    '--ui-surface': surface,
    '--ui-surface-elevated': elevated,

    '--ui-text': textDefault,
    '--ui-text-muted': textMuted,
    '--ui-text-subtle': textSubtle,
    '--ui-text-inverse': textInverse,
    '--ui-text-disabled': textDisabled,

    '--ui-border': borderDefault,
    '--ui-border-muted': borderSubtle,
    '--ui-border-strong': borderStrong,

    '--ui-interactive': primary,
    '--ui-interactive-hover': primaryHover,
    '--ui-interactive-active': primaryActive,
    '--ui-on-interactive': onPrimary,
    '--ui-interactive-light': primaryLight,

    '--ui-focus-ring': focusRingAlpha,

    '--ui-status-success': palette.green[400],
    '--ui-status-success-muted': palette.green[950],
    '--ui-status-warning': palette.amber[300],
    '--ui-status-warning-muted': palette.amber[950],
    '--ui-status-danger': destructive,
    '--ui-status-danger-muted': palette.red[950],
    '--ui-status-info': palette.blue[300],
    '--ui-status-info-muted': palette.blue[950],

    '--ui-overlay': overlay,
    '--ui-shadow-color': getRgbaValue('#000000', 0.45),
    '--ui-shadow-default': getRgbaValue('#000000', 0.2),
    '--ui-shadow-hover': getRgbaValue(lighten(primary, 0.3), 0.25),
    '--ui-scrollbar-track': palette.gray[800],
    '--ui-scrollbar-thumb': palette.gray[600],
    '--ui-scrollbar-thumb-hover': palette.gray[500],

    '--ui-table-header-color': textMuted,
    '--ui-table-cell-color': textDefault,
    '--ui-table-border': borderDefault,
    '--ui-table-hover': getRgbaValue('#ffffff', 0.04),
    '--ui-table-header-bg': surface,

    '--ui-pagination-active-bg': primaryLight,
    '--ui-pagination-active-color': lighten(primary, 0.25),
    '--ui-pagination-text': textMuted,
    '--ui-pagination-muted': palette.gray[500],

    '--ui-badge-icon-border': primary,
    '--ui-badge-icon-color': primary,
    '--ui-badge-hover': primaryLight,
    '--ui-badge-count-bg': destructive,

    '--ui-breadcrumb-active': lighten(primary, 0.15),
    '--ui-breadcrumb-inactive': palette.gray[500],
    '--ui-breadcrumb-link': lighten(primary, 0.15),
    '--ui-breadcrumb-separator': palette.gray[500],

    '--ui-upload-icon-color': lighten(primary, 0.2),
    '--ui-upload-border': borderDefault,
    '--ui-upload-border-error': destructive,
    '--ui-upload-bg': surface,
    '--ui-upload-preview-bg': elevated,

    '--ui-btn-accent': lighten(palette.Iris, 0.15),
    '--ui-btn-accent-light': primaryLight,
    '--ui-btn-outline-border': getRgbaValue(lighten(palette.Iris, 0.15), 0.3),
    '--ui-btn-red-outline-border': getRgbaValue(destructive, 0.3),
    '--ui-btn-red-shadow-hover': getRgbaValue(palette.gray[600], 0.35),
    '--ui-btn-red-shadow-active': getRgbaValue(destructive, 0.44),
    '--ui-btn-shadow': getRgbaValue(palette.Iris, 0.24),
    '--ui-btn-disabled-bg': inset,
    '--ui-btn-disabled-color': getRgbaValue(palette.gray[500], 0.5),

    '--ui-input-border': borderDefault,
    '--ui-input-border-focus': palette.blue[400],
    '--ui-input-shadow': getRgbaValue(palette.gray[700], 0.2),

    '--ui-tab-active-bg': primary,
    '--ui-tab-inactive-bg': inset,
    '--ui-tab-active-color': textDefault,
    '--ui-tab-inactive-color': textMuted,

    '--ui-separator-color': borderDefault,

    '--ui-spinner-border': primary,
    '--ui-spinner-track': borderDefault,

    '--ui-background-page': page,
    '--ui-background-surface': surface,
    '--ui-background-elevated': elevated,
    '--ui-background-inset': inset,

    '--ui-text-default': textDefault,
    '--ui-text-on-primary': onPrimary,

    '--ui-border-default': borderDefault,
    '--ui-border-subtle': borderSubtle,
    '--ui-border-focus': focusRingAlpha,

    '--ui-interactive-primary': primary,
    '--ui-interactive-primary-hover': primaryHover,
    '--ui-interactive-primary-active': primaryActive,
    '--ui-interactive-secondary': secondary,
    '--ui-interactive-secondary-hover': secondaryHover,
    '--ui-interactive-secondary-active': secondaryActive,
    '--ui-on-secondary': onSecondary,

    '--ui-destructive': destructive,
    '--ui-destructive-hover': destructiveHover,

    '--ui-status-success-bg': palette.green[900],
    '--ui-status-warning-bg': palette.amber[900],
    '--ui-status-error': destructive,
    '--ui-status-error-bg': palette.red[950],
    '--ui-status-info-bg': palette.blue[950],

    '--ui-chart-text': palette.gray[300],
    '--ui-chart-grid': palette.gray[800],
    '--ui-chart-axis': palette.gray[600],
    '--ui-chart-tooltip-bg': palette.gray[800],
    '--ui-chart-tooltip-border': palette.gray[600],
    '--ui-chart-positive': '#4ade80',
    '--ui-chart-negative': '#f87171',
    '--ui-chart-1': lighten(palette.Purple, 0.2),
    '--ui-chart-2': lighten(palette.Iris, 0.2),
    '--ui-chart-3': '#facc15',
    '--ui-chart-4': lighten(palette.SkyBlue, 0.15),
    '--ui-chart-5': lighten(palette.LightBlue, 0.1),
    '--ui-chart-6': lighten(palette.Pink, 0.15),
    '--ui-chart-7': '#4ade80',
    '--ui-chart-8': lighten(palette.Secondary, 0.15),
  };
}

/** Maximum-contrast palette (light). */
export function highContrastAppearance(brand: BrandConfig): Record<string, string> {
  const primary = brand.primary;
  const secondary = brand.secondary ?? '#006B7D';
  const destructive = '#BA2323';

  const primaryHover = darken(primary, 0.12);
  const primaryActive = darken(primary, 0.2);
  const secondaryHover = darken(secondary, 0.12);
  const secondaryActive = darken(secondary, 0.2);
  const destructiveHover = darken(destructive, 0.12);

  const onPrimary = pickOnPrimary(primary);
  const onSecondary = pickOnPrimary(secondary);

  const page = '#ffffff';
  const surface = '#ffffff';
  const elevated = '#ffffff';
  const inset = '#f0f0f0';
  const overlay = getRgbaValue('#000000', 0.8);

  const textDefault = '#000000';
  const textMuted = '#000000';
  const textSubtle = '#1a1a1a';
  const textInverse = '#ffffff';
  const textDisabled = '#4d4d4d';

  const borderDefault = '#000000';
  const borderSubtle = '#000000';
  const borderStrong = '#000000';
  const focusRing = '#0000ff';

  return {
    ...fontVars(brand),

    '--ui-background': page,
    '--ui-background-muted': inset,
    '--ui-surface': surface,
    '--ui-surface-elevated': elevated,

    '--ui-text': textDefault,
    '--ui-text-muted': textMuted,
    '--ui-text-subtle': textSubtle,
    '--ui-text-inverse': textInverse,
    '--ui-text-disabled': textDisabled,

    '--ui-border': borderDefault,
    '--ui-border-muted': borderSubtle,
    '--ui-border-strong': borderStrong,

    '--ui-interactive': primary,
    '--ui-interactive-hover': primaryHover,
    '--ui-interactive-active': primaryActive,
    '--ui-on-interactive': onPrimary,

    '--ui-focus-ring': focusRing,

    '--ui-status-success': '#0d6e0d',
    '--ui-status-success-muted': '#e8f5e8',
    '--ui-status-warning': '#8a5b00',
    '--ui-status-warning-muted': '#fff4d6',
    '--ui-status-danger': destructive,
    '--ui-status-danger-muted': '#ffecec',
    '--ui-status-info': '#0044aa',
    '--ui-status-info-muted': '#e6eefc',

    '--ui-overlay': overlay,
    '--ui-shadow-color': '#000000',
    '--ui-shadow-hover': getRgbaValue(primary, 0.3),
    '--ui-scrollbar-track': '#e0e0e0',
    '--ui-scrollbar-thumb': '#666666',
    '--ui-scrollbar-thumb-hover': '#333333',

    '--ui-background-page': page,
    '--ui-background-surface': surface,
    '--ui-background-elevated': elevated,
    '--ui-background-inset': inset,

    '--ui-text-default': textDefault,
    '--ui-text-on-primary': onPrimary,

    '--ui-border-default': borderDefault,
    '--ui-border-subtle': borderSubtle,
    '--ui-border-focus': focusRing,

    '--ui-interactive-primary': primary,
    '--ui-interactive-primary-hover': primaryHover,
    '--ui-interactive-primary-active': primaryActive,
    '--ui-interactive-secondary': secondary,
    '--ui-interactive-secondary-hover': secondaryHover,
    '--ui-interactive-secondary-active': secondaryActive,
    '--ui-on-secondary': onSecondary,

    '--ui-destructive': destructive,
    '--ui-destructive-hover': destructiveHover,

    '--ui-table-header-color': textDefault,
    '--ui-table-cell-color': textDefault,
    '--ui-table-border': borderDefault,
    '--ui-table-hover': '#e6e6e6',
    '--ui-table-header-bg': surface,

    '--ui-pagination-active-bg': darken(primary, 0.4),
    '--ui-pagination-active-color': primary,
    '--ui-pagination-text': textDefault,
    '--ui-pagination-muted': textMuted,

    '--ui-breadcrumb-active': primary,
    '--ui-breadcrumb-inactive': textMuted,
    '--ui-breadcrumb-link': primary,
    '--ui-breadcrumb-separator': textMuted,

    '--ui-btn-accent': primary,
    '--ui-btn-accent-light': darken(primary, 0.4),
    '--ui-btn-outline-border': getRgbaValue(primary, 0.3),
    '--ui-btn-red-outline-border': getRgbaValue(destructive, 0.3),
    '--ui-btn-red-shadow-hover': getRgbaValue('#b3b3b3', 0.24),
    '--ui-btn-red-shadow-active': getRgbaValue(destructive, 0.44),
    '--ui-btn-shadow': getRgbaValue(primary, 0.3),
    '--ui-btn-disabled-bg': inset,
    '--ui-btn-disabled-color': textDisabled,

    '--ui-input-border': borderDefault,
    '--ui-input-border-focus': '#0044aa',
    '--ui-input-shadow': getRgbaValue('#000000', 0.15),

    '--ui-tab-active-bg': primary,
    '--ui-tab-inactive-bg': inset,
    '--ui-tab-active-color': onPrimary,
    '--ui-tab-inactive-color': textDefault,

    '--ui-separator-color': borderDefault,

    '--ui-spinner-border': primary,
    '--ui-spinner-track': borderDefault,

    '--ui-status-success-bg': '#e8f5e8',
    '--ui-status-warning-bg': '#fff4d6',
    '--ui-status-error': destructive,
    '--ui-status-error-bg': '#ffecec',
    '--ui-status-info-bg': '#e6eefc',

    '--ui-chart-text': '#000000',
    '--ui-chart-grid': '#d4d4d4',
    '--ui-chart-axis': '#000000',
    '--ui-chart-tooltip-bg': '#ffffff',
    '--ui-chart-tooltip-border': '#000000',
    '--ui-chart-positive': '#0d6e0d',
    '--ui-chart-negative': '#ba2323',
    '--ui-chart-1': '#5b21b6',
    '--ui-chart-2': '#1e3a8a',
    '--ui-chart-3': '#a16207',
    '--ui-chart-4': '#0369a1',
    '--ui-chart-5': '#1d4ed8',
    '--ui-chart-6': '#be185d',
    '--ui-chart-7': '#166534',
    '--ui-chart-8': '#115e59',
  };
}
